// hooks/useKeycloakTokenHandler.js
import { useEffect } from "react";
import useAuth from "@/hooks/useAuth";
import * as Sentry from "@sentry/react";
import { diagnosticEventLog } from "@/shared/services/diagnostic";
import { handleTokenRefreshFailure } from "@/initKeycloak";

/**
 * This hook refreshes the Keycloak token when the document
 * when the document visibility changes
 * */
export const useKeycloakTokenHandler = () => {
  const { keycloakInstance } = useAuth();

  // Visibility change listener
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === "visible" && keycloakInstance) {
        console.info("Visibility changed to visible.");
        Sentry.addBreadcrumb({ category: "auth", message: "Tab became visible", level: "info" });

        if (!keycloakInstance.authenticated) {
          console.log("User not authenticated.");

          if (keycloakInstance.loginRequired) {
            console.log("Attempting logging in...");
            keycloakInstance.login();
          }

          return;
        }

        console.log("User is authenticated. Proceeding with token refresh...");

        if (!keycloakInstance.isTokenExpired(60)) {
          console.info("Token is still valid. No need to refresh.");
          return;
        }

        console.info("Token is expired. Attempting token refresh...");
        diagnosticEventLog.record("auth:app", "warn", "visibility-token-expired");

        keycloakInstance
          .updateToken(60)
          .then((refreshed) => {
            if (!refreshed && !keycloakInstance.authenticated) {
              handleTokenRefreshFailure("visibility:not-refreshed");
            }
          })
          .catch((error) => {
            handleTokenRefreshFailure("visibility:error", error);
          });
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [keycloakInstance]);

  // Periodic forced token refresh (every 2 minutes).
  // The Keycloak login status iframe only checks the SSO cookie, which stays
  // valid even when individual client sessions are terminated (e.g. concurrent
  // session policy killing the oldest session). This forced refresh is the only
  // way to detect server-side session revocation from the client.
  useEffect(() => {
    if (!keycloakInstance?.authenticated) return;

    const REFRESH_INTERVAL_MS = 2 * 60 * 1000;

    const intervalId = setInterval(() => {
      // Skip while the tab is hidden — browsers throttle background fetches and
      // the .catch() branch can't distinguish that from a real invalid_grant, so
      // running this in a hidden tab produces false-positive forced logouts.
      // The visibilitychange handler above re-checks the token on tab return,
      // which is when a multi-session kick actually matters for the user.
      if (document.visibilityState === "hidden") {
        Sentry.addBreadcrumb({
          category: "auth",
          message: "Periodic refresh skipped — tab hidden",
          level: "info",
        });
        return;
      }

      keycloakInstance
        .updateToken(-1)
        .then(() => {
          Sentry.addBreadcrumb({ category: "auth", message: "Periodic refresh succeeded", level: "info" });
        })
        .catch(() => {
          handleTokenRefreshFailure("periodic:error");
        });
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [keycloakInstance]);
};

export default useKeycloakTokenHandler;
